import React, { useMemo, useState } from 'react';
import { isEmpty, pickBy } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { firmSummaryUrl } from 'common/config/urls';
import { FIRM_FUND_STEPPER_BASE_STATE, FIRM_FUND_STEPPER_TITLES } from 'common/constants/firms';
import { Firm } from 'common/types/firm';
import { FirmForm } from 'components/Forms';
import { FirmFormState } from 'components/Forms/FirmForm/FirmForm.props';
import Stepper from 'components/Stepper';
import { useFundForm } from 'pages/Funds/forms/FundDialogForm/hooks';
import { FirmHk } from 'services/hooks';
import { FirmStepperProps, StepperState } from './FirmStepper.props';

const FirmStepper = ({ isVisible, onClose }: FirmStepperProps) => {
  const [stepperState, setStepperState] = useState<StepperState>({
    firm: FIRM_FUND_STEPPER_BASE_STATE as FirmFormState,
    fund: FIRM_FUND_STEPPER_BASE_STATE as FirmFormState,
  });

  const { firm } = stepperState;

  const navigate = useNavigate();

  const { useCreateNewFirm } = FirmHk;
  const [, createFirm] = useCreateNewFirm();

  const { formState: fundFormState, FundForm } = useFundForm({ fund: undefined });

  const isStepperEmpty = useMemo(
    () => isEmpty(pickBy(firm.values)) && isEmpty(pickBy(fundFormState.values)),
    [firm, fundFormState]
  );

  const handleFormStateChange = (step: string, formState: FirmFormState) => {
    setStepperState(previousState => ({
      ...previousState,
      [step]: {
        ...previousState[step as keyof StepperState],
        ...formState,
      },
    }));
  };

  const onSubmit = async () => {
    const { values: firmData } = firm;
    const { values: fundData } = fundFormState;

    const allData = {
      ...firmData,
      fund: fundData,
    } as Firm;

    // Create Firm
    const data = (await createFirm(allData)) as any;
    const { response } = data;

    if (response?.status) {
      const errors = response.body;

      if (errors) {
        const { fund: fundErrors, ...firmErrors } = errors;

        setStepperState({
          ...stepperState,
          firm: {
            ...stepperState.firm,
            dbErrors: firmErrors,
          },
          fund: {
            ...stepperState.firm,
            dbErrors: fundErrors,
          },
        });

        return false;
      }
    }

    navigate(firmSummaryUrl(data.slug));
    return true;
  };

  const steps = [
    {
      id: 'firm',
      title: 'Firm',
      isValid: firm.isValid,
      component: (
        <FirmForm
          defaultFormState={firm as unknown as FirmFormState}
          onFormChange={state => handleFormStateChange('firm', state)}
        />
      ),
    },
    {
      id: 'fund',
      title: 'Fund',
      isValid: fundFormState.isValid,
      component: FundForm,
    },
  ];

  return (
    <Stepper
      id="firm"
      isVisible={isVisible}
      title="Create New Firm"
      stepperTitles={FIRM_FUND_STEPPER_TITLES}
      steps={steps}
      onClose={onClose}
      onSubmit={onSubmit}
      confirmCancel={!isStepperEmpty}
    />
  );
};

export default FirmStepper;
